/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import humanize from 'humanize-plus';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { HUMaterialTable, HUMaterialTableProps } from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../i18n';
import { SuperuserDatasetApiService } from '../../services/SuperuserDatasetApiService';
import { EntityId } from '../../types/api/EntityId';
import { SUDataset } from '../../types/superuser/dataset/SUDataset';
import { fetchAdminDatasets } from '../../utils/queryFns';
import { reactToUpdateAdminDatasets } from '../../utils/reactions';
import { searchSubstring } from '../../utils/searchSubstring';
import { deleteAdminDatasetInitialValues, invisibleRowBC, notExistedRowBC } from './AdminDatasetsPage.consts';
import { useAdminDatasetsPageStyles } from './AdminDatasetsPage.styles';
import { DeleteAdminDatasetDataType } from './AdminDatasetsPage.types';
import { DatasetsDetailPanel } from './components/DatasetsDetailPanel/DatasetsDetailPanel';
import { DatasetStatusLabel } from './components/DatasetStatusLabel/DatasetStatusLabel';
import { EditDatasetForm } from './components/EditDatasetForm/EditDatasetForm';

export function AdminDatasetsPage() {
  const classes = useAdminDatasetsPageStyles();
  const { t, i18n } = useTranslation(Namespaces.Admin);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false);
  const toggleEditForm = useCallback(() => {
    setIsEditFormOpen(s => !s);
  }, []);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const [deleteDatasetData, setDeleteDatasetData] = useState<DeleteAdminDatasetDataType>(
    deleteAdminDatasetInitialValues,
  );

  const handleDelete = (dataset: SUDataset) => {
    setDeleteDatasetData({
      id: dataset.id,
      name: dataset.name,
      isModalOpened: true,
    });
  };

  const queryDatasets = useQuery(fetchAdminDatasets.key, fetchAdminDatasets.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const datasets = useMemo(() => queryDatasets.data?.payload ?? [], [queryDatasets]);

  const sortedDatasets = useMemo(() => _.sortBy(_.sortBy(datasets, ['updatedAt']).reverse(), ['exists']), [datasets]);

  const isDatasetsLoading = useMemo(() => queryDatasets.status === 'loading', [queryDatasets.status]);

  const isError = useMemo(
    () => queryDatasets.error && queryDatasets.status === 'error',
    [queryDatasets.error, queryDatasets.status],
  );

  const [dataset, setDataset] = useState<SUDataset | null>(null);

  const handleEdit = useCallback(
    (dataset: SUDataset) => {
      setDataset(dataset);
      toggleEditForm();
    },
    [toggleEditForm],
  );

  const handleAdd = useCallback(() => {
    setDataset(null);
    toggleEditForm();
  }, [toggleEditForm]);

  const getSize = (size: string) => humanize.fileSize(+size * Math.pow(1024, 2));

  const prepareDate = useCallback(
    (date: string) => {
      return DateTime.fromISO(date).setLocale(i18n.language).toFormat('LLL');
    },
    [i18n.language],
  );

  const onConfirmDeleteDataset = async () => {
    try {
      setIsLoading(true);
      setDeleteDatasetData({
        ...deleteAdminDatasetInitialValues,
        isModalOpened: false,
      });
      await SuperuserDatasetApiService.deleteDataset({
        datasetId: deleteDatasetData.id,
      });
      await reactToUpdateAdminDatasets();
      setIsLoading(false);
      setDeleteDatasetData(deleteAdminDatasetInitialValues);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setIsOpenErrorModal(true);
    }
  };

  const verifyDataset = async (id: EntityId) => {
    try {
      setIsLoading(true);
      await SuperuserDatasetApiService.verifyDataset({ datasetId: id });
      await reactToUpdateAdminDatasets();
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setIsOpenErrorModal(true);
    }
  };

  const tableConfig = useMemo<HUMaterialTableProps<SUDataset>>(
    () => ({
      columns: [
        {
          title: t('datasets.table.labels.name'),
          render: rowData => {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {rowData.name}
                <DatasetStatusLabel dataset={rowData} />
              </div>
            );
          },
          cellStyle: {
            color: '#1F3F6D',
            fontWeight: 500,
          },
          customFilterAndSearch: (substring, rowData) => searchSubstring(substring, rowData.name),
        },
        {
          title: t('datasets.table.labels.final_size'),
          render: rowData => getSize(rowData.finalSizeMb),
        },
        {
          title: t('datasets.table.labels.created_at'),
          render: rowData => prepareDate(rowData.createdAt),
        },
        {
          title: t('datasets.table.labels.updated_at'),
          render: rowData => prepareDate(rowData.updatedAt),
        },
      ],
      data: sortedDatasets,
      title: t('datasets.title'),
      actions: [
        rowData => {
          return {
            icon: () => <RefreshIcon />,
            tooltip: t('datasets.table.labels.verify'),
            onClick: () => verifyDataset(rowData.id),
          };
        },
        rowData => {
          return {
            icon: () => <EditIcon />,
            tooltip: t('datasets.table.labels.edit'),
            onClick: () => handleEdit(rowData),
          };
        },
        rowData => {
          const isDisabled = rowData.clusters > 0;
          return {
            icon: () => <DeleteIcon />,
            tooltip: isDisabled ? t('datasets.table.labels.not_deletable') : t('datasets.table.labels.delete'),
            disabled: isDisabled,
            onClick: () => !isDisabled && handleDelete(rowData),
          };
        },
        {
          icon: () => <AddBoxIcon />,
          tooltip: t('datasets.table.labels.add'),
          isFreeAction: true,
          onClick: handleAdd,
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
        rowStyle: rowData => ({
          backgroundColor: !rowData.exists ? notExistedRowBC : !rowData.visible ? invisibleRowBC : '#FFF',
        }),
      },
      detailPanel: [
        {
          tooltip: t('datasets.table.show_more'),
          render: ({ rowData }) => (
            <DatasetsDetailPanel rowData={rowData} getSize={getSize} prepareDate={prepareDate} />
          ),
        },
      ],
    }),
    [t, sortedDatasets, handleAdd, prepareDate, handleEdit],
  );

  return (
    <>
      {isDatasetsLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isLoading} delay={50}>
          <HUMaterialTable {...tableConfig} />
          <EditDatasetForm isOpen={isEditFormOpen} closeOverlay={toggleEditForm} dataset={dataset} />
        </HUSpin>
      )}
      <SimpleConfirmModal
        open={deleteDatasetData.isModalOpened}
        message={t('datasets.confirmations.delete_dataset', {
          name: deleteDatasetData.name,
        })}
        onCancel={() => setDeleteDatasetData(deleteAdminDatasetInitialValues)}
        onConfirm={onConfirmDeleteDataset}
      />
      <SimpleNotifyModal
        open={isOpenErrorModal}
        message={t(`${Namespaces.Common}:errors.unexpected_error`)}
        onOk={() => setIsOpenErrorModal(false)}
      />
    </>
  );
}
